"use client";

import {JSX, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {Spinner} from "@/shared/ui/feedback/spinner";

import type {ImageSliderItem} from "./image-slider";

import "./styles/gallery-image-with-spinner.scss";

export interface GalleryImageWithSpinnerProps extends ImageSliderItem {
    width: number;
    height: number;
    className?: string;
    sizes?: string;
    loading?: "lazy" | "eager";
    quality?: number;
    priority?: boolean;
    /** When true, wrapper fills the parent (e.g. slide inner); when false, uses min-height for list layout */
    fillContainer?: boolean;
    noImgixCrop?: boolean;
}

/**
 * Renders a gallery image with a centered spinner until the image has loaded.
 * Used in ImageGalleryModal (list and slider) to show loading state.
 */
export function GalleryImageWithSpinner({
    src,
    alt,
    width,
    height,
    className = "",
    sizes,
    loading = "lazy",
    quality,
    priority = false,
    fillContainer = false,
    noImgixCrop,
}: GalleryImageWithSpinnerProps): JSX.Element {
    const [isLoaded, setIsLoaded] = useState(false);
    const wrapperClassName = `gallery-image-with-spinner ${fillContainer ? "gallery-image-with-spinner--fill" : ""}`;

    return (
        <div className={wrapperClassName}>
            {!isLoaded && (
                <div className="gallery-image-with-spinner__spinner-wrap" aria-hidden="true">
                    <Spinner size="medium" />
                </div>
            )}
            <SmartImage
                src={src}
                alt={alt}
                width={width}
                height={height}
                className={`gallery-image-with-spinner__image ${className}`}
                sizes={sizes}
                loading={loading}
                quality={quality}
                priority={priority}
                noImgixCrop={noImgixCrop}
                onLoad={() => setIsLoaded(true)}
                style={{opacity: isLoaded ? 1 : 0}}
            />
        </div>
    );
}
